import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Trophy, CheckCircle, Award, Flag, Star, Phone } from "lucide-react";
import { useState } from "react";
import SEOHead from "@/components/SEOHead";

const IMAGES = {
  hero: "/volleyball-celebration.jpg",
  highfive: "/volleyball-highfive.jpg",
  kids: "/volleyball-kids.jpg",
  action: "/volleyball-action.jpg",
  team: "/volleyball-team.jpg",
  training: "/volleyball-training.jpg",
  huddle: "/volleyball-huddle.jpg",
  lineup: "/volleyball-lineup.jpg",
};

const details = [
  { icon: Users, label: "Yaş Grubu", value: "7-14 Yaş" },
  { icon: Trophy, label: "Seviye", value: "Başlangıç - İleri" },
];

const highlights = [
  "Temel voleybol teknikleri (servis, pas, smaç)",
  "Takım oyunu ve strateji geliştirme",
  "Yaş grubuna uygun antrenman programları",
  "İl ve bölge müsabakalarında temsil",
  "Fiziksel gelişim ve kondisyon çalışması",
  "Mini voleybol (küçük yaş grupları)",
  "Pozisyon bazlı özel çalışmalar",
  "Video analizi ile gelişim takibi",
];

const federationAdvantages = [
  { icon: Award, title: "Lisanslı Sporcu", desc: "TVF onaylı lisanslı sporcu olma imkânı ile resmi müsabakalara katılım hakkı kazanın." },
  { icon: Flag, title: "Federasyon Ligleri", desc: "Türkiye Voleybol Federasyonu liglerinde kulübümüz adına mücadele edin." },
  { icon: Trophy, title: "Turnuva Katılımı", desc: "İl, bölge ve ulusal düzeyde turnuva ve şampiyonalara katılım fırsatı." },
  { icon: Star, title: "Bireysel Gelişim", desc: "Her sporcuya özel gelişim planı ve düzenli performans değerlendirmesi." },
];

const trainingProgram = [
  { level: "Başlangıç", ages: "7-9 Yaş", desc: "Temel motor beceriler, topa alışma, basit pas teknikleri ve eğlenceli oyunlarla voleybola giriş.", color: "bg-gold" },
  { level: "Gelişim", ages: "10-12 Yaş", desc: "Servis, pas, manşet teknikleri, takım oyunu temelleri ve mini müsabaka deneyimi.", color: "bg-burgundy" },
  { level: "İleri", ages: "13-14 Yaş", desc: "İleri teknik ve taktik çalışmalar, smaç-blok teknikleri, müsabaka hazırlığı ve turnuva katılımı.", color: "bg-[#1A1A1A]" },
];

const galleryItems = [
  { img: IMAGES.lineup, title: "Takım Kadromuz", span: "md:col-span-2 md:row-span-2" },
  { img: IMAGES.action, title: "Antrenman" },
  { img: IMAGES.training, title: "Teknik Çalışma" },
  { img: IMAGES.huddle, title: "Takım Ruhu" },
  { img: IMAGES.team, title: "Grup Antrenmanı" },
  { img: IMAGES.highfive, title: "Zafer Sevinci", span: "md:col-span-2" },
  { img: IMAGES.kids, title: "Minik Sporcular" },
  { img: IMAGES.hero, title: "Kutlama" },
];

export default function VolleyballPage() {
  const [lightboxImg, setLightboxImg] = useState(null);

  return (
    <div data-testid="volleyball-page" className="pt-20">
      <SEOHead
        title={"Voleybol Kursu Ataşehir, Kadıköy, Maltepe - TVF Lisanslı Eğitim"}
        description={"İRÜ Spor Kulübü voleybol kursu. Ataşehir, Kadıköy, Maltepe bölgesinde TVF lisanslı voleybol eğitimi. 7-14 yaş grubu, profesyonel antrenörler, federasyon liglerinde mücadele imkânı. Anadolu yakası voleybol spor okulu."}
        keywords={"voleybol kursu Kadıköy, voleybol eğitimi Ataşehir, voleybol spor okulu Maltepe, Anadolu yakası voleybol, çocuk voleybol kursu İstanbul, TVF lisanslı voleybol kulübü, voleybol antrenmanı, mini voleybol, genç voleybol, Bostancı voleybol, voleybol okulu İstanbul, lisanslı voleybolcu olma"}
        canonical="/branslarimiz/voleybol"
        ogImage="/volleyball-celebration.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Voleybol Kursu - İRÜ Spor Kulübü",
          "description": "Ataşehir, Kadıköy, Maltepe bölgesinde TVF lisanslı voleybol eğitimi. 7-14 yaş grubu için profesyonel antrenörler eşliğinde voleybol kursu.",
          "provider": { "@type": "SportsOrganization", "name": "İRÜ Spor Kulübü" },
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "onsite",
            "courseSchedule": { "@type": "Schedule" },
            "location": {
              "@type": "Place",
              "name": "İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi",
              "address": { "@type": "PostalAddress", "addressLocality": "İstanbul", "addressRegion": "Kadıköy", "addressCountry": "TR" }
            }
          },
          "audience": { "@type": "EducationalAudience", "educationalRole": "student", "requiredMinAge": 7, "requiredMaxAge": 14 }
        }}
      />
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Voleybol" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/90 via-[#1A1A1A]/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <Link to="/branslarimiz" className="inline-flex items-center gap-1 text-xs font-accent uppercase tracking-widest text-white/60 hover:text-white/80 transition-colors mb-4">
            {"Branşlarımız"} <span className="mx-1">/</span>
          </Link>
          <div className="flex items-center gap-5 mb-4">
            <img src="/tvf-logo.png" alt="TVF Logo" className="w-16 h-16 sm:w-20 sm:h-20 object-contain bg-white/10 backdrop-blur-sm rounded-full p-1.5" />
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white">
                Voleybol
              </h1>
              <p className="text-xs sm:text-sm font-accent uppercase tracking-widest text-gold mt-1">{"TVF Onaylı Lisanslı Kulüp"}</p>
            </div>
          </div>
          <p className="mt-4 text-base md:text-lg text-white/70 max-w-lg leading-relaxed">
            {"Takım ruhu, teknik beceri ve müsabaka heyecanı bir arada. Profesyonel antrenörler eşliğinde voleybolun keyfini çıkarın."}
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            {details.map((d) => (
              <div key={d.label} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2">
                <d.icon className="w-4 h-4 text-gold" strokeWidth={1.5} />
                <div>
                  <span className="text-[10px] text-white/50 font-accent uppercase tracking-wider">{d.label}</span>
                  <p className="text-sm text-white/90 font-medium">{d.value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 mt-10">
            <Link to="/on-kayit">
              <Button data-testid="vol-register-btn" className="bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm px-8 py-6">
                {"Kayıt Ol"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/iletisim">
              <Button data-testid="vol-info-btn" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wider text-sm px-8 py-6">
                <Phone className="w-4 h-4 mr-2" /> Bilgi Al
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Federasyon & Lisans */}
      <section className="py-16 lg:py-20 bg-[#1A1A1A]" data-testid="volleyball-federation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="shrink-0">
              <img src="/tvf-logo.png" alt={"Türkiye Voleybol Federasyonu"} className="w-24 h-24 md:w-32 md:h-32 object-contain" />
            </div>
            <div className="text-center md:text-left">
              <span className="font-accent text-sm uppercase tracking-widest text-gold">{"Federasyon Üyeliği"}</span>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white mt-1">
                {"Türkiye Voleybol Federasyonu Lisanslı Kulüp"}
              </h2>
              <p className="mt-3 text-white/60 leading-relaxed max-w-2xl">
                {"İRÜ Spor Kulübü, Türkiye Voleybol Federasyonu'na kayıtlı ve lisanslı bir kulüptür. Sporcularımız TVF organizasyonlarında resmi olarak müsabakalara katılım hakkı kazanmaktadır."}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {federationAdvantages.map((item) => (
              <div key={item.title} className="border border-white/10 hover:border-gold/30 p-6 transition-all duration-300 group">
                <item.icon className="w-8 h-8 text-gold mb-4 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
                <h3 className="font-heading text-base font-bold uppercase tracking-wider text-white">{item.title}</h3>
                <p className="mt-2 text-sm text-white/50 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hakkında */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-accent text-sm uppercase tracking-widest text-burgundy">{"Branş Detayı"}</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2">
                {"Ataşehir, Kadıköy, Maltepe Voleybol Kursu"}
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">
                {"İRÜ Spor Kulübü voleybol branşında, Ataşehir, Kadıköy ve Maltepe bölgesinden gelen sporcularımıza temel teknik becerilerin yanında takım oyunu, strateji ve müsabaka deneyimi kazandırmaktayız. Profesyonel antrenörlerimiz eşliğinde her yaş grubuna uygun eğitim programları sunulmaktadır."}
              </p>
              <p className="mt-4 text-gray-600 leading-relaxed">
                {"Sporcularımız düzenli antrenmanlarla hem bireysel yeteneklerini hem de takım içi uyumlarını geliştirmektedir. İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi'ndeki modern tesislerimizde, TVF liglerinde ve il/bölge müsabakalarına katılım sağlayarak gerçek rekabet deneyimi kazanmaktadırlar."}
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {highlights.map((h) => (
                  <div key={h} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-burgundy mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span className="text-sm text-gray-600">{h}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[3/4] overflow-hidden">
                <img src={IMAGES.highfive} alt="Voleybol high-five" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden">
                <img src={IMAGES.kids} alt={"Voleybol çocuklar"} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eğitim Seviyeleri */}
      <section className="py-20 lg:py-28 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-burgundy">{"Eğitim Programı"}</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2 mb-12">
            {"Seviyeye Göre Programlar"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {trainingProgram.map((p) => (
              <div key={p.level} className="border border-zinc-200 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300 overflow-hidden bg-white">
                <div className={`${p.color} px-6 py-4 flex items-center justify-between`}>
                  <span className="font-heading text-base font-bold uppercase tracking-wider text-white">{p.level}</span>
                  <span className="font-accent text-xs text-white/80 bg-white/20 px-3 py-1">{p.ages}</span>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-600 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Galeri */}
      <section className="py-20 lg:py-28 bg-white" data-testid="volleyball-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Galeri</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2 mb-12">
            Voleyboldan Kareler
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]">
            {galleryItems.map((item) => (
              <div
                key={item.title}
                className={`relative group overflow-hidden cursor-pointer ${item.span || ""}`}
                onClick={() => setLightboxImg(item.img)}
                data-testid={`gallery-item-${item.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                  <p className="font-heading text-xs uppercase tracking-wider text-white">{item.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImg(null)}
          data-testid="gallery-lightbox"
        >
          <img src={lightboxImg} alt="Galeri" className="max-w-full max-h-[85vh] object-contain" />
          <button className="absolute top-6 right-6 text-white/70 hover:text-white text-3xl font-light">&times;</button>
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-burgundy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">{"Voleybol Ailemize Katılın"}</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">
            {"Lisanslı sporcu olma fırsatını kaçırmayın. Federasyon liglerinde kulübümüz adına mücadele edin."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/on-kayit">
              <Button data-testid="vol-cta-register" className="bg-white text-burgundy hover:bg-gray-100 font-heading uppercase tracking-wider text-sm px-10 py-6">
                {"Ön Kayıt Formu"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/iletisim">
              <Button data-testid="vol-cta-info" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wider text-sm px-10 py-6">
                <Phone className="w-4 h-4 mr-2" /> Bilgi Al
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
